import * as React from 'react'
import { default as ReactMarkdown } from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import Image from 'next/image'
import Link from 'next/link'

// eslint-disable-next-line no-restricted-imports
import { Variant } from '@mui/material/styles/createTypography'
import { styled } from '@mui/material/styles'
import { useTheme } from '@mui/material'

import CodeBlock from 'components/atomic/atoms/code-block-async'

const ImageWrapper = styled('div')`
    background: ${({ theme }) => theme.palette.common.white};
    border-radius: ${({ theme }) => `${theme.shape.borderRadius}px`};
    width: 100%;
    position: relative;
    overflow: hidden;

    > span {
        position: unset !important;
    }

    .image {
        object-fit: contain;
        width: 100% !important;
        position: relative !important;
        height: unset !important;
    }
`

interface MarkdownViewerProps {
    children: string | null
    style?: React.CSSProperties
}

const MarkdownViewer = ({ children, style }: MarkdownViewerProps) => {
    const { typography, palette } = useTheme()

    const getStyle = React.useCallback(
        (type: Variant) => {
            const typographyItem = typography[type]
            const mdStyle = {
                fontSize: typographyItem.fontSize,
                fontWeight: typographyItem.fontWeight,
                lineHeight: typographyItem.lineHeight,
                letterSpacing: typographyItem?.letterSpacing,
                color: typographyItem.color,
                fontFamily: typography.fontFamily,
            }
            return {
                ...mdStyle,
                ...style,
            }
        },
        [typography, style],
    )

    return (
        <ReactMarkdown
            rehypePlugins={[rehypeRaw]}
            components={{
                h1: ({ children, ...props }) => (
                    <h1 style={getStyle('h1')} {...props}>
                        {children}
                    </h1>
                ),
                h2: ({ children, ...props }) => (
                    <h2 style={getStyle('h2')} {...props}>
                        {children}
                    </h2>
                ),
                h3: ({ children, ...props }) => (
                    <h3 style={getStyle('h3')} {...props}>
                        {children}
                    </h3>
                ),
                h4: ({ children, ...props }) => (
                    <h4 style={getStyle('h4')} {...props}>
                        {children}
                    </h4>
                ),
                h5: ({ children, ...props }) => (
                    <h5 style={getStyle('h5')} {...props}>
                        {children}
                    </h5>
                ),
                h6: ({ children, ...props }) => (
                    <h5 style={getStyle('h6')} {...props}>
                        {children}
                    </h5>
                ),
                p: ({ children, node, ...props }: { children: any; node: any }) => {
                    if (node.children[0]?.tagName === 'img') {
                        const image = node.children[0]
                        const metastring = image.properties.alt
                        const alt = metastring?.replace(/ *\{[^)]*\} */g, '')
                        // const metaWidth = metastring.match(/{([^}]+)x/)
                        // const metaHeight = metastring.match(/x([^}]+)}/)
                        // const width = metaWidth ? metaWidth[1] : '850'
                        // const height = metaHeight ? metaHeight[1] : '432'
                        // const isPriority = metastring?.toLowerCase().match('{priority}')
                        const hasCaption = metastring?.toLowerCase().includes('{caption:')
                        const caption = metastring?.match(/{caption: (.*?)}/)?.pop()

                        return (
                            <div>
                                <ImageWrapper>
                                    <Image src={image.properties.src} alt={alt} layout="fill" className="image" />
                                </ImageWrapper>
                                {hasCaption ? (
                                    <div className="caption" aria-label={caption}>
                                        {caption}
                                    </div>
                                ) : null}
                            </div>
                        )
                    }
                    return (
                        <p style={getStyle('body1')} {...props}>
                            {children}
                        </p>
                    )
                },
                a: ({ children, ...props }) => {
                    return (
                        <Link href={props.href || ''} target="_blank" rel="noopener noreferrer">
                            <a
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    ...getStyle('body1'),
                                    color: palette.primary.main,
                                    textDecoration: 'underline',
                                }}>
                                {children}
                            </a>
                        </Link>
                    )
                },
                code: ({ children, className, ...props }) => {
                    const language = (className && className.replace('language-', '')) || 'plaintext'
                    return (
                        <CodeBlock language={language} {...props}>
                            {(children.length ? children[0] : children) as string}
                        </CodeBlock>
                    )
                },
            }}>
            {children || ''}
        </ReactMarkdown>
    )
}

export default MarkdownViewer
